import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import { catchError, concatMap, filter, map, tap } from 'rxjs/operators';
import { fromSession } from '.';
import { InfoService } from '../info-store/info.service';
import { selectSelectedTenantId } from '../tenant-store/tenant.selectors';
import { isNotNull } from '../utils';
import { ILoginCredentials } from './interfaces/login-credentials.interface';
import { ISession } from './interfaces/session.interface';
import * as SessionActions from './session.actions';
import { SessionApiService } from './session.api.service';

const SESSION = 'session';

@Injectable()
export class SessionEffects {
  private readonly actions$ = inject(Actions);
  private readonly sessionApi = inject(SessionApiService);
  private readonly infoService = inject(InfoService);
  private readonly store = inject<Store<fromSession.State>>(Store);

  register$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(SessionActions.registerRequest),
      concatMap(({ data }) =>
        this.sessionApi.register(data).pipe(
          map(() =>
            SessionActions.loginRequest({
              credentials: {
                email: data.email,
                password: data.password,
              } as ILoginCredentials,
            })
          ),
          catchError((error: HttpErrorResponse) => {
            this.infoService.addMessage({
              message: '$register.error-creating-account',
              severity: 'error',
              error,
            });
            return of(SessionActions.loadSessionFailure({ error }));
          })
        )
      )
    );
  });

  validateRegister$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(SessionActions.validateRegister),
      concatMap(({ token }) =>
        this.sessionApi.validateRegister(token).pipe(
          map(() => {
            this.infoService.addMessage({
              message: '$validate.success',
              severity: 'success',
            });
            return SessionActions.validateRegisterSuccess();
          }),
          catchError((error: HttpErrorResponse) => {
            this.infoService.addMessage({
              message: '$validate.error',
              severity: 'error',
              error,
            });
            return of(SessionActions.validateRegisterFailure({ error }));
          })
        )
      )
    );
  });

  login$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(SessionActions.loginRequest),
      concatMap(({ credentials }) =>
        this.sessionApi.login(credentials).pipe(
          map((session) => {
            localStorage.setItem(SESSION, JSON.stringify(session));
            return SessionActions.loadSessionSuccess({ session });
          }),
          catchError((error: HttpErrorResponse) => {
            if ((error?.error?.message as string)?.includes('password') || error?.status === 404) {
              this.infoService.addMessage({
                message: '$login.invalid-credentials',
                severity: 'warn',
              });
            } else {
              this.infoService.addMessage({
                message: '$login.error-logging-in',
                severity: 'warn',
                error,
              });
            }
            return of(SessionActions.loadSessionFailure({ error }));
          })
        )
      )
    );
  });

  loginWithToken$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(SessionActions.loginWithToken),
      concatMap(({ tenantId, token }) =>
        this.sessionApi.loginWithToken(token, tenantId).pipe(
          map((session) => {
            session.token = token;
            localStorage.setItem(SESSION, JSON.stringify(session));
            return SessionActions.loadSessionSuccess({ session });
          }),
          catchError((error: HttpErrorResponse) => {
            this.infoService.addMessage({
              message: '$login.error-logging-in',
              severity: 'warn',
              error,
            });

            return of(SessionActions.loadSessionFailure({ error }));
          })
        )
      )
    );
  });

  loadSession$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(SessionActions.loadSession),
      map(() => {
        const sessionJson = localStorage.getItem(SESSION);
        const session = (sessionJson ? JSON.parse(sessionJson) : null) as ISession | null;

        return SessionActions.loadSessionSuccess({ session });
      }),
      catchError((error: unknown) => {
        console.error(error);
        this.infoService.addMessage({
          message: '$login.error-recovering-session',
          severity: 'error',
          error,
        });
        return of(SessionActions.loadSessionFailure({ error }));
      })
    );
  });

  // Keep the cached copy in step with the store, so a reload does not bring back stale permissions.
  sessionRefreshed$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(SessionActions.sessionRefreshed),
        tap(({ session }) => {
          const sessionJson = localStorage.getItem(SESSION);
          if (!sessionJson) return;
          const stored = JSON.parse(sessionJson) as ISession;
          localStorage.setItem(SESSION, JSON.stringify({ ...stored, ...session }));
        })
      );
    },
    { dispatch: false }
  );

  editUserProfile$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(SessionActions.editProfile),
      concatLatestFrom(() => this.store.select(selectSelectedTenantId).pipe(filter(isNotNull))),
      concatMap(([{ profileInfo }, tenantId]) =>
        this.sessionApi.editProfile(profileInfo, tenantId.toString()).pipe(
          map((user) => {
            this.infoService.addMessage({
              message: '$user.success-updating-profile',
              severity: 'success',
            });
            return SessionActions.editProfileSuccess({ user });
          }),
          catchError((error: unknown) => {
            this.infoService.addMessage({
              message: '$user.error-updating-profile',
              severity: 'error',
            });
            return of(SessionActions.editProfileFailure({ error }));
          })
        )
      )
    );
  });

  getProfile$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(SessionActions.getProfile),
      concatLatestFrom(() => this.store.select(selectSelectedTenantId).pipe(filter(isNotNull))),
      concatMap(([, tenantId]) =>
        this.sessionApi.getProfile(tenantId.toString()).pipe(
          map((user) => {
            const sessionJson = localStorage.getItem('session');
            const session = (sessionJson ? JSON.parse(sessionJson) : null) as ISession | null;

            if (session?.user) {
              session.user = { ...session.user, ...user, roles: session.user.roles };
              localStorage.setItem('session', JSON.stringify(session));
            }
            return SessionActions.getProfileSuccess({ user });
          }),
          catchError((error: unknown) => {
            return of(SessionActions.getProfileFailure({ error }));
          })
        )
      )
    );
  });

  removeSession$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(SessionActions.removeSession),
      concatMap(() => this.sessionApi.logout()),
      map(() => {
        localStorage.clear();
        sessionStorage.clear();
        return SessionActions.loadSessionSuccess({ session: null });
      }),
      catchError((error: unknown) => {
        console.error(error);
        this.infoService.addMessage({
          message: '$login.error-logging-out',
          severity: 'error',
          error,
        });
        return of(SessionActions.loadSessionFailure({ error }));
      })
    );
  });

  resetPasswordRequest$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(SessionActions.resetPasswordRequest),
      concatMap(({ email }) =>
        this.sessionApi.resetPasswordRequest(email).pipe(
          map(() => {
            this.infoService.addMessage({
              message: '$login.recover-password-success',
              severity: 'success',
            });
            return SessionActions.resetPasswordRequestSuccess();
          }),
          catchError((error: unknown) => {
            this.infoService.addMessage({
              message: '$login.recover-password-error',
              severity: 'warn',
              error,
            });
            return of(SessionActions.resetPasswordRequestFailure({ error }));
          })
        )
      )
    );
  });

  resetPassword$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(SessionActions.resetPassword),
      concatMap(({ newCredentials }) =>
        this.sessionApi.resetPassword(newCredentials).pipe(
          map((session) => {
            this.infoService.addMessage({
              message: '$login.reset-password.success',
              severity: 'success',
            });
            localStorage.setItem(SESSION, JSON.stringify(session));
            return SessionActions.loadSessionSuccess({ session });
          }),
          catchError((error: unknown) => {
            this.infoService.addMessage({
              message: '$login.reset-password.error',
              severity: 'error',
              error,
            });
            return of(SessionActions.loadSessionFailure({ error }));
          })
        )
      )
    );
  });

  changePassword$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(SessionActions.changePassword),
      concatLatestFrom(() => this.store.select(selectSelectedTenantId).pipe(filter(isNotNull))),
      concatMap(([{ newCredentials }, tenantId]) =>
        this.sessionApi.changePassword(newCredentials, tenantId).pipe(
          map((newCredentials) => {
            this.infoService.addMessage({
              message: '$login.reset-password.success',
              severity: 'success',
            });
            return SessionActions.changePasswordSuccess({ newCredentials });
          }),
          catchError((error: unknown) => {
            this.infoService.addMessage({
              message: '$login.reset-password.error',
              severity: 'error',
              error,
            });
            return of(SessionActions.changePasswordFailure({ error }));
          })
        )
      )
    );
  });

  createPassword$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(SessionActions.createPassword),
      concatLatestFrom(() => this.store.select(selectSelectedTenantId).pipe(filter(isNotNull))),
      concatMap(([{ createPassword }, tenantId]) =>
        this.sessionApi.createPassword(createPassword, tenantId).pipe(
          map(() => {
            this.infoService.addMessage({
              message: '$login.create-password-success',
              severity: 'success',
            });
            return SessionActions.createPasswordSuccess();
          }),
          catchError((error: unknown) => {
            this.infoService.addMessage({
              message: '$login.create-password-failure',
              severity: 'error',
            });
            return of(SessionActions.createPasswordFailure({ error }));
          })
        )
      )
    );
  });

  impersonateUser$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(SessionActions.impersonateUser),
      concatLatestFrom(() => this.store.select(selectSelectedTenantId).pipe(filter(isNotNull))),
      concatMap(([{ email }, tenantId]) =>
        this.sessionApi.impersonate(email, tenantId).pipe(
          map((session) => {
            localStorage.setItem(SESSION, JSON.stringify(session));
            return SessionActions.impersonateUserSuccess({ session });
          }),
          catchError((error: HttpErrorResponse) => {
            this.infoService.addMessage({
              message: '$login.impersonate-error',
              severity: 'error',
              error,
            });
            return of(SessionActions.impersonateUserFailure({ error }));
          })
        )
      )
    );
  });

  httpResult$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(
        SessionActions.loadSessionSuccess,
        SessionActions.loadSessionFailure,
        SessionActions.removeSession,
        SessionActions.changePasswordSuccess,
        SessionActions.changePasswordFailure,
        SessionActions.editProfileFailure,
        SessionActions.editProfileSuccess,
        SessionActions.getProfileSuccess,
        SessionActions.getProfileFailure,
        SessionActions.createPasswordSuccess,
        SessionActions.createPasswordFailure,
        SessionActions.impersonateUserSuccess,
        SessionActions.impersonateUserFailure,
        SessionActions.resetPasswordRequestSuccess,
        SessionActions.resetPasswordRequestFailure
      ),
      map((res) =>
        SessionActions.httpResult({
          action: res.type,
          error: (res as { error?: unknown })?.error ?? null,
        })
      )
    );
  });
}
